import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Video, X, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';
import UpdateDealsOnboardingModal from './UpdateDealsOnboardingModal';
import AIPAdvancedFilters, { initialFilters } from './AIPAdvancedFilters';
import AIPVideoCard from './AIPVideoCard';
import AIPMinimizedVideoCard from './AIPMinimizedVideoCard';

const ITEMS_PER_PAGE = 20;

const tryParse = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return null; }
};

/**
 * Normalize a reverse-scan product (flat result_data) into the same shape
 * that storefront videos use (separate JSON-string fields).
 */
const normalizeReverseProduct = (product) => {
    // Reverse scan API returns data in same shape as storefront,
    // just with `video_data` (singular) instead of `videos_data`.
    return {
        ...product,
        status: 'completed',
        videos_data: product.video_data || product.videos_data || null,
    };
};

/**
 * AIPVideosViewer - shared viewer for both Storefront and Reverse Scan.
 *
 * Props:
 *   storefront – storefront object (storefront mode)
 *   search     – search object (reverse scan mode)
 *   onClose    – close callback
 *
 * Pass either `storefront` or `search`, not both.
 */
const AIPVideosViewer = ({ storefront, search, onClose, user }) => {
    const mode = storefront ? 'storefront' : 'reverse';
    const sourceId = storefront?.id || search?.id;
    const sourceStatus = storefront?.status || search?.status;

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({ ...initialFilters });
    const [isCompactView, setIsCompactView] = useState(true);

    // Live update state
    const [tempVideos, setTempVideos] = useState([]);
    const [hasNewVideos, setHasNewVideos] = useState(false);
    const [ignoredNewVideos, setIgnoredNewVideos] = useState(false);
    const [pendingIncoming, setPendingIncoming] = useState([]);

    // Creator Connection per ASIN: undefined | 'loading' | 'error' | { count, highestCommission }
    const [ccMap, setCcMap] = useState({});
    const ccRef = useRef({});
    const ccRunRef = useRef(0);

    const shouldPoll = mode === 'storefront'
        ? sourceStatus === 'active'
        : (sourceStatus === 'processing' || sourceStatus === 'pending');

    useEffect(() => {
        setVideos([]);
        setTempVideos([]);
        setHasNewVideos(false);
        setIgnoredNewVideos(false);
        loadVideos();
        const interval = setInterval(() => {
            if (shouldPoll) loadVideos(true);
        }, 20000);
        return () => clearInterval(interval);
    }, [sourceId, sourceStatus]);

    // Reset page on filter change
    useEffect(() => { setCurrentPage(1); }, [filters]);

    const loadVideos = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const url = mode === 'storefront'
                ? API_BASE + `/api/aip/storefront/videos/${sourceId}`
                : API_BASE + `/api/aip/reverse-scan/searches/${sourceId}/products`;
            const response = await authService.authenticatedFetch(url);
            const data = await response.json();
            if (response.ok) {
                const raw = mode === 'storefront'
                    ? (data.videos || [])
                    : (data.products || []).map(normalizeReverseProduct);
                const incoming = raw;
                setVideos(prev => {
                    if (prev.length === 0) return incoming;
                    const prevMap = new Map(prev.map(v => [v.id, v]));
                    const newOnes = incoming.filter(v => !prevMap.has(v.id));
                    const statusChanged = incoming.filter(v => {
                        const old = prevMap.get(v.id);
                        return old && old.status !== v.status;
                    });
                    if (newOnes.length > 0 || statusChanged.length > 0) {
                        setTempVideos([...newOnes, ...statusChanged]);
                        setPendingIncoming(incoming);
                        setHasNewVideos(true);
                    }
                    return prev;
                });
            }
        } catch (error) {
            console.error('Failed to load videos:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // ── Group items ──
    // Storefront: 1 row per VIDEO (video is primary entity)
    // Reverse:    1 row per ASIN  (product is primary entity)
    const grouped = useMemo(() => {
        if (mode === 'storefront') {
            // Each video is its own row; collect sibling videos sharing the same ASIN
            const asinMap = new Map();
            for (const v of videos) {
                const key = v.asin || v.id;
                if (!asinMap.has(key)) asinMap.set(key, []);
                asinMap.get(key).push(v);
            }
            return videos.map(v => {
                const siblings = asinMap.get(v.asin || v.id) || [v];
                return { asin: v.asin || v.id, videos: siblings, primary: v };
            });
        }
        // Reverse: group by ASIN (1 product per ASIN from API, so usually 1:1)
        const map = new Map();
        for (const v of videos) {
            const key = v.asin || v.id;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(v);
        }
        return Array.from(map.entries()).map(([asin, vids]) => {
            const sorted = [...vids].sort((a, b) => {
                if (a.status === 'completed' && b.status !== 'completed') return -1;
                if (a.status !== 'completed' && b.status === 'completed') return 1;
                return new Date(b.created_at) - new Date(a.created_at);
            });
            return { asin, videos: sorted, primary: sorted[0] };
        });
    }, [videos, mode]);

    // ── Extract filter options ──
    const { categories, brands, creators } = useMemo(() => {
        const cats = new Set();
        const brs = new Set();
        const crs = new Set();
        for (const g of grouped) {
            const product = tryParse(g.primary.product_data);
            if (product?.category) cats.add(product.category);
            if (product?.brand) brs.add(product.brand);
            for (const v of g.videos) {
                const vm = tryParse(v.video_meta);
                if (vm?.vendor_name) crs.add(vm.vendor_name);
            }
        }
        return {
            categories: [...cats].sort(),
            brands: [...brs].sort(),
            creators: [...crs].sort(),
        };
    }, [grouped]);

    // ── Helper: parse duration "M:SS" to seconds ──
    const parseDuration = (dur) => {
        if (!dur) return null;
        const parts = String(dur).split(':');
        if (parts.length === 2) return Number(parts[0]) * 60 + Number(parts[1]);
        if (parts.length === 3) return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
        return Number(dur) || null;
    };

    // ── Apply filters ──
    const filtered = useMemo(() => {
        return grouped.filter(g => {
            const v = g.primary;
            const videoMeta = tryParse(v.video_meta);
            const commission = tryParse(v.commission_data);
            const product = tryParse(v.product_data);
            const sales = tryParse(v.sales_data);

            // Status
            if (filters.status !== 'all' && v.status !== filters.status) return false;

            // Search
            if (filters.search) {
                const q = filters.search.toLowerCase();
                const fields = [
                    v.asin, v.title, videoMeta?.video_title, videoMeta?.vendor_name,
                    product?.brand, product?.category
                ].filter(Boolean).map(s => s.toLowerCase());
                if (!fields.some(f => f.includes(q))) return false;
            }

            // Numeric range filters
            const price = commission?.price != null ? Number(commission.price) : null;
            const commRate = commission?.commission_rate != null ? Number(commission.commission_rate) : null;
            const commPerSale = commission?.commission_per_sale != null ? Number(commission.commission_per_sale) : null;
            const estSales = sales?.estimated_monthly_sales != null ? Number(sales.estimated_monthly_sales) : null;
            const estRev = sales?.estimated_monthly_revenue != null ? Number(sales.estimated_monthly_revenue) : null;
            const bsr = sales?.sales_rank != null ? Number(sales.sales_rank) : null;
            const rankDrops30 = sales?.sales_rank_drops_30 != null ? Number(sales.sales_rank_drops_30) : null;
            const rankDrops90 = sales?.sales_rank_drops_90 != null ? Number(sales.sales_rank_drops_90) : null;
            const variations = product?.variations != null ? Number(product.variations) : null;
            const totalOffers = product?.total_offers != null ? Number(product.total_offers) : null;
            const durationSec = parseDuration(videoMeta?.duration);
            const hearts = videoMeta?.heart_count != null ? Number(videoMeta.heart_count) : null;

            if (filters.minPrice && (price == null || price < Number(filters.minPrice))) return false;
            if (filters.maxPrice && (price == null || price > Number(filters.maxPrice))) return false;
            if (filters.minCommissionRate && (commRate == null || commRate < Number(filters.minCommissionRate))) return false;
            if (filters.maxCommissionRate && (commRate == null || commRate > Number(filters.maxCommissionRate))) return false;
            if (filters.minCommissionPerSale && (commPerSale == null || commPerSale < Number(filters.minCommissionPerSale))) return false;
            if (filters.minEstSales && (estSales == null || estSales < Number(filters.minEstSales))) return false;
            if (filters.maxEstSales && (estSales == null || estSales > Number(filters.maxEstSales))) return false;
            if (filters.minEstRevenue && (estRev == null || estRev < Number(filters.minEstRevenue))) return false;
            if (filters.maxEstRevenue && (estRev == null || estRev > Number(filters.maxEstRevenue))) return false;
            if (filters.minBSR && (bsr == null || bsr < Number(filters.minBSR))) return false;
            if (filters.maxBSR && (bsr == null || bsr > Number(filters.maxBSR))) return false;
            if (filters.minRankDrops30 && (rankDrops30 == null || rankDrops30 < Number(filters.minRankDrops30))) return false;
            if (filters.minRankDrops90 && (rankDrops90 == null || rankDrops90 < Number(filters.minRankDrops90))) return false;
            if (filters.minHearts && (hearts == null || hearts < Number(filters.minHearts))) return false;
            if (filters.maxHearts && (hearts == null || hearts > Number(filters.maxHearts))) return false;

            // Videos data (inf videos on product page)
            const videosData = tryParse(v.videos_data);

            const totalInfVideos = videosData?.total_inf_videos != null ? Number(videosData.total_inf_videos) : null;
            if (filters.minTotalInfVideos && (totalInfVideos == null || totalInfVideos < Number(filters.minTotalInfVideos))) return false;
            if (filters.maxTotalInfVideos && (totalInfVideos == null || totalInfVideos > Number(filters.maxTotalInfVideos))) return false;

            const topInf = videosData?.upper_carousel?.avg_influencer_per_fetch != null ? Number(videosData.upper_carousel.avg_influencer_per_fetch) : null;
            const topTotal = videosData?.upper_carousel?.avg_videos_per_fetch != null ? Number(videosData.upper_carousel.avg_videos_per_fetch) : null;
            const topBrand = topTotal != null && topInf != null ? topTotal - topInf : null;
            if (filters.minTopInfVideos && (topInf == null || topInf < Number(filters.minTopInfVideos))) return false;
            if (filters.maxTopInfVideos && (topInf == null || topInf > Number(filters.maxTopInfVideos))) return false;
            if (filters.minTopBrandVideos && (topBrand == null || topBrand < Number(filters.minTopBrandVideos))) return false;
            if (filters.maxTopBrandVideos && (topBrand == null || topBrand > Number(filters.maxTopBrandVideos))) return false;

            const bottomInf = videosData?.bottom_carousel?.avg_influencer_per_fetch != null ? Number(videosData.bottom_carousel.avg_influencer_per_fetch) : null;
            const bottomTotal = videosData?.bottom_carousel?.avg_videos_per_fetch != null ? Number(videosData.bottom_carousel.avg_videos_per_fetch) : null;
            const bottomBrand = bottomTotal != null && bottomInf != null ? bottomTotal - bottomInf : null;
            if (filters.minBottomInfVideos && (bottomInf == null || bottomInf < Number(filters.minBottomInfVideos))) return false;
            if (filters.maxBottomInfVideos && (bottomInf == null || bottomInf > Number(filters.maxBottomInfVideos))) return false;
            if (filters.minBottomBrandVideos && (bottomBrand == null || bottomBrand < Number(filters.minBottomBrandVideos))) return false;
            if (filters.maxBottomBrandVideos && (bottomBrand == null || bottomBrand > Number(filters.maxBottomBrandVideos))) return false;

            // Has Main Videos (upper carousel avg_videos_per_fetch > 0)
            if (filters.hasMainVideos === 'yes') {
                const upperAvg = videosData?.upper_carousel?.avg_videos_per_fetch;
                if (upperAvg == null || Number(upperAvg) <= 0) return false;
            }
            if (filters.hasMainVideos === 'no') {
                const upperAvg = videosData?.upper_carousel?.avg_videos_per_fetch;
                if (upperAvg != null && Number(upperAvg) > 0) return false;
            }

            // Dropdowns
            if (filters.category && product?.category !== filters.category) return false;
            if (filters.brand && product?.brand !== filters.brand) return false;
            if (filters.amazonSells === 'yes' && product?.az_sells_it !== true) return false;
            if (filters.amazonSells === 'no' && product?.az_sells_it !== false) return false;
            if (filters.minRating && (Number(product?.rating) || 0) < Number(filters.minRating)) return false;
            if (filters.minReviews && (Number(product?.review_count) || 0) < Number(filters.minReviews)) return false;

            // Creator Connection
            if (filters.cc && filters.cc !== 'all') {
                const c = ccMap[v.asin];
                if (!c || typeof c !== 'object') return false;
                if (filters.cc === 'yes' && !(c.count > 0)) return false;
                if (filters.cc === 'no' && c.count > 0) return false;
            }

            return true;
        });
    }, [grouped, filters, ccMap]);

    // ── Sort ──
    const sorted = useMemo(() => {
        const arr = [...filtered];

        switch (filters.sortBy) {
            case 'price_asc':
                arr.sort((a, b) => (Number(tryParse(a.primary.commission_data)?.price) || 0) - (Number(tryParse(b.primary.commission_data)?.price) || 0));
                break;
            case 'price_desc':
                arr.sort((a, b) => (Number(tryParse(b.primary.commission_data)?.price) || 0) - (Number(tryParse(a.primary.commission_data)?.price) || 0));
                break;
            case 'commission':
                arr.sort((a, b) => (Number(tryParse(b.primary.commission_data)?.commission_rate) || 0) - (Number(tryParse(a.primary.commission_data)?.commission_rate) || 0));
                break;
            case 'commission_per_sale':
                arr.sort((a, b) => (Number(tryParse(b.primary.commission_data)?.commission_per_sale) || 0) - (Number(tryParse(a.primary.commission_data)?.commission_per_sale) || 0));
                break;
            case 'est_sales':
                arr.sort((a, b) => (Number(tryParse(b.primary.sales_data)?.estimated_monthly_sales) || 0) - (Number(tryParse(a.primary.sales_data)?.estimated_monthly_sales) || 0));
                break;
            case 'est_revenue':
                arr.sort((a, b) => (Number(tryParse(b.primary.sales_data)?.estimated_monthly_revenue) || 0) - (Number(tryParse(a.primary.sales_data)?.estimated_monthly_revenue) || 0));
                break;
            case 'bsr':
                arr.sort((a, b) => (Number(tryParse(a.primary.sales_data)?.sales_rank) || Infinity) - (Number(tryParse(b.primary.sales_data)?.sales_rank) || Infinity));
                break;
            case 'rank_drops_30':
                arr.sort((a, b) => (Number(tryParse(b.primary.sales_data)?.sales_rank_drops_30) || 0) - (Number(tryParse(a.primary.sales_data)?.sales_rank_drops_30) || 0));
                break;
            case 'hearts':
                arr.sort((a, b) => (Number(tryParse(b.primary.video_meta)?.heart_count) || 0) - (Number(tryParse(a.primary.video_meta)?.heart_count) || 0));
                break;
            case 'video_count':
                arr.sort((a, b) => b.videos.length - a.videos.length);
                break;
            case 'duration':
                arr.sort((a, b) => (parseDuration(tryParse(b.primary.video_meta)?.duration) || 0) - (parseDuration(tryParse(a.primary.video_meta)?.duration) || 0));
                break;
            case 'inf_videos':
                arr.sort((a, b) => (Number(tryParse(b.primary.videos_data)?.total_inf_videos) || 0) - (Number(tryParse(a.primary.videos_data)?.total_inf_videos) || 0));
                break;
            case 'uploaded_at':
                arr.sort((a, b) => new Date(b.primary.created_at) - new Date(a.primary.created_at));
                break;
            default:
                arr.sort((a, b) => {
                    if (a.primary.status === 'completed' && b.primary.status !== 'completed') return -1;
                    if (a.primary.status !== 'completed' && b.primary.status === 'completed') return 1;
                    return new Date(b.primary.created_at) - new Date(a.primary.created_at);
                });
        }
        return arr;
    }, [filtered, filters.sortBy]);

    // ── Pagination ──
    const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);
    const paginated = sorted.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

    // ── Creator Connection queue: visible first, then next pages, 3 at a time ──
    const visibleKey = [...new Set(paginated.map(g => g.primary.asin).filter(Boolean))].join(',');
    useEffect(() => {
        const run = ++ccRunRef.current;
        const visible = paginated.map(g => g.primary.asin);
        const rest = [...sorted, ...grouped].map(g => g.primary.asin);
        const queue = [...new Set([...visible, ...rest])].filter(a => a && ccRef.current[a] === undefined);
        if (!queue.length) return;

        (async () => {
            for (let i = 0; i < queue.length; i += 3) {
                await new Promise(r => setTimeout(r, 300 + Math.random() * 200));
                if (ccRunRef.current !== run) return;
                const batch = queue.slice(i, i + 3).filter(a => ccRef.current[a] === undefined);
                if (!batch.length) continue;
                batch.forEach(a => { ccRef.current[a] = 'loading'; });
                setCcMap({ ...ccRef.current });

                await Promise.all(batch.map(async asin => {
                    try {
                        const r = await authService.authenticatedFetch(`${API_BASE}/api/amazon/aip/card-data/${asin}/cc`);
                        const data = r.ok ? await r.json() : null;
                        ccRef.current[asin] = {
                            count: data?.cc?.count ?? 0,
                            highestCommission: data?.cc?.highestCommission ?? null,
                            campaigns: data?.cc?.campaigns || [],
                        };
                    } catch {
                        ccRef.current[asin] = 'error';
                    }
                }));
                setCcMap({ ...ccRef.current });
            }
        })();
        return () => { ccRunRef.current++; };
    }, [visibleKey, grouped.length]);

    // Extract display name
    const displayName = mode === 'storefront'
        ? (storefront.store_name || (() => {
            try {
                const match = storefront.storefront_url.match(/\/shop\/([^/?#]+)/i);
                return match ? match[1] : 'Storefront';
            } catch { return 'Storefront'; }
        })())
        : 'AIP Search';

    // Placement name - from influencer_metadata.vendor_name, with localStorage override
    const LS_PLACEMENT_KEY = 'aip_placement_name';
    const inflMeta = mode === 'storefront' ? tryParse(storefront.influencer_metadata) : null;
    const defaultPlacementName = storefront?.store_name || inflMeta?.vendor_name || '';
    const [placementName, setPlacementName] = useState(() => {
        return user?.my_aip_storefront_name || localStorage.getItem(LS_PLACEMENT_KEY) || defaultPlacementName;
    });
    useEffect(() => {
        (async () => {
            try {
                const r = await authService.authenticatedFetch(API_BASE + '/api/aip/my-storefront');
                const d = r.ok ? await r.json() : null;
                if (d?.storefront_name) setPlacementName(d.storefront_name);
            } catch { /* keep current name */ }
        })();
    }, []);

    const updatePlacementName = (name) => {
        setPlacementName(name);
        if (name && name !== defaultPlacementName) {
            localStorage.setItem(LS_PLACEMENT_KEY, name);
        } else {
            localStorage.removeItem(LS_PLACEMENT_KEY);
        }
    };

    const scanType = mode === 'storefront' ? 'storefront' : 'reverse';

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 relative">
            <UpdateDealsOnboardingModal show={hasNewVideos} scanType={scanType} />

            {/* New videos toast */}
            {hasNewVideos && !ignoredNewVideos && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4">
                    <span>🎉 {tempVideos.length} new video{tempVideos.length > 1 ? 's' : ''} found!</span>
                    <button
                        onClick={() => {
                            setVideos(pendingIncoming);
                            setTempVideos([]);
                            setPendingIncoming([]);
                            setHasNewVideos(false);
                            setCurrentPage(1);
                        }}
                        className="bg-teal-500 hover:bg-teal-400 px-3 py-1 rounded-full text-sm font-semibold"
                    >
                        Update
                    </button>
                    <button
                        onClick={() => { setIgnoredNewVideos(true); setHasNewVideos(false); }}
                        className="text-gray-400 hover:text-white text-sm"
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Close */}
            <button onClick={onClose} className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
            </button>

            {/* Header */}
            <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Video className="text-teal-600" size={24} />
                    {displayName} - {mode === 'storefront' ? 'Videos' : 'Products'}
                </h2>
                <p className="text-sm text-gray-500">
                    {mode === 'storefront'
                        ? `${videos.length} review${videos.length !== 1 ? 's' : ''}`
                        : `${videos.length} product${videos.length !== 1 ? 's' : ''}`
                    }
                    {mode === 'reverse' && search?.upload_method && ` · ${search.upload_method === 'keepa_finder' ? 'Parameters' : 'CSV'}`}
                    {(() => {
                        const all = [...new Set(grouped.map(g => g.primary.asin).filter(Boolean))];
                        const done = all.filter(a => ccMap[a] && ccMap[a] !== 'loading').length;
                        return all.length > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-700">
                                {done < all.length && <RefreshCw size={11} className="animate-spin" />}
                                · CC checked {done}/{all.length}
                            </span>
                        );
                    })()}
                </p>
            </div>

            {/* Filters */}
            <AIPAdvancedFilters
                filters={filters}
                setFilters={setFilters}
                categories={categories}
                brands={brands}
                creators={creators}
                isCompactView={isCompactView}
                setIsCompactView={setIsCompactView}
                totalCount={grouped.length}
                filteredCount={sorted.length}
            />

            {/* Content */}
            {loading ? (
                <div className="text-center py-16">
                    <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={48} />
                    <p className="text-gray-600">Loading videos...</p>
                </div>
            ) : sorted.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                    {videos.length === 0
                        ? `No ${mode === 'storefront' ? 'videos' : 'products'} found yet. The scan may still be in progress.`
                        : `No ${mode === 'storefront' ? 'videos' : 'products'} match your filters.`
                    }
                </div>
            ) : (
                <>
                    <div className="space-y-3">
                        {paginated.map((group, idx) => (
                            isCompactView ? (
                                <AIPMinimizedVideoCard
                                    key={group.primary.id}
                                    videos={group.videos}
                                    primary={group.primary}
                                    mode={mode}
                                    storefrontName={placementName}
                                    rawStorefrontName={defaultPlacementName}
                                    onPlacementNameChange={updatePlacementName}
                                    cc={ccMap[group.primary.asin]}
                                />
                            ) : (
                                <AIPVideoCard
                                    key={group.primary.id}
                                    videos={group.videos}
                                    primary={group.primary}
                                    mode={mode}
                                    storefrontName={placementName}
                                    rawStorefrontName={defaultPlacementName}
                                    onPlacementNameChange={updatePlacementName}
                                    cc={ccMap[group.primary.asin]}
                                />
                            )))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t">
                            <span className="text-sm text-gray-500">
                                Showing {(safePage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(safePage * ITEMS_PER_PAGE, sorted.length)} of {sorted.length}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={safePage <= 1}
                                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                                    .reduce((acc, p, idx, arr) => {
                                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, i) =>
                                        p === '...' ? (
                                            <span key={`dot-${i}`} className="px-1 text-gray-400">...</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => setCurrentPage(p)}
                                                className={`w-8 h-8 rounded-lg text-sm font-semibold transition ${p === safePage
                                                    ? 'bg-teal-600 text-white'
                                                    : 'hover:bg-gray-100 text-gray-600'
                                                    }`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={safePage >= totalPages}
                                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AIPVideosViewer;
